import { useEffect, useMemo, useRef, useState } from 'react'
import { Form, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { Page } from '#app/components/page.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Divider } from '#app/components/ui/divider.tsx'
import { type Route } from '#app/routes/mocra+/_form+/+types/suppliers-testing.ts'
import { RepositoryEntrySchema } from '#app/routes/mocra+/_form+/components/repositories/schema.ts'
import {
	createRepositoryDraft,
	isRepositorySystemValue,
	repositoryDraftToEntry,
	repositoryEntryToDraft,
	sanitizeRepositoryDraft,
	type RepositoryDraft,
	type RepositorySystemValue,
} from '#app/routes/mocra+/_form+/components/repositories/types.ts'
import { supplierDefinition } from '#app/routes/mocra+/_form+/components/suppliers/definition.ts'
import { SupplierEditOverlay } from '#app/routes/mocra+/_form+/components/suppliers/edit.tsx'
import {
	SupplierEntrySchema,
	type SupplierEntry,
} from '#app/routes/mocra+/_form+/components/suppliers/schema.ts'
import {
	createSupplierDraft,
	supplierDraftToEntry,
	supplierEntryToDraft as ignoredSupplierEntryToDraft,
	isSupplierRiskLevelValue,
	type SupplierDraft,
} from '#app/routes/mocra+/_form+/components/suppliers/types.ts'
import { testRoutineDefinition } from '#app/routes/mocra+/_form+/components/test-routines/definition.ts'
import { TestRoutineEditOverlay } from '#app/routes/mocra+/_form+/components/test-routines/edit.tsx'
import {
	TestRoutineEntrySchema,
	type TestRoutineEntry,
} from '#app/routes/mocra+/_form+/components/test-routines/schema.ts'
import {
	createTestRoutineDraft,
	testRoutineDraftToEntry,
	testRoutineEntryToDraft as ignoredTestRoutineEntryToDraft,
	isRoutineTestFrequencyValue,
	isRoutineTestNameValue,
	type TestRoutineDraft,
} from '#app/routes/mocra+/_form+/components/test-routines/types.ts'
import {
	buildStepPathFromId,
	getNextFormStepId,
} from '#app/routes/mocra+/_form+/form-steps.ts'
import { useEntityCollection } from '#app/routes/mocra+/_form+/hooks/use-entity-collection.ts'
import { useSectionForm } from '#app/routes/mocra+/_form+/hooks/use-section-form.ts'
import { createRouteSectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'
import { CriticalSuppliersSection } from '#app/routes/mocra+/_form+/suppliers-testing/critical-suppliers-section.tsx'
import { RoutineTestingSection } from '#app/routes/mocra+/_form+/suppliers-testing/routine-testing-section.tsx'
import { SafetyRepositorySection } from '#app/routes/mocra+/_form+/suppliers-testing/safety-repository-section.tsx'

const SuppliersTestingSchema = z.object({
	suppliers: z.array(SupplierEntrySchema).default([]),
	safetyRepository: RepositoryEntrySchema,
	routineTests: z.array(TestRoutineEntrySchema).default([]),
})

export type SuppliersTestingFormValues = z.infer<typeof SuppliersTestingSchema>

const DEFAULT_REPOSITORY_ENTRY = repositoryDraftToEntry(createRepositoryDraft())

const DEFAULT_VALUES: SuppliersTestingFormValues = {
	suppliers: [],
	safetyRepository: DEFAULT_REPOSITORY_ENTRY,
	routineTests: [],
}

const SECTION_KEY = 'suppliersTesting'
const STEP_ID = 'suppliers-testing'
const SUPPLIERS_TESTING_RESOURCES = createRouteSectionResources({
	route: '/mocra/suppliers-testing',
})

export const meta: Route.MetaFunction = () => [
	{ title: 'MoCRA — Suppliers & Testing' },
]

export default function SuppliersAndTesting() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const [editingSupplierIndex, setEditingSupplierIndex] = useState<
		number | null
	>(null)
	const [editingRoutineIndex, setEditingRoutineIndex] = useState<number | null>(
		null,
	)
	const form = useSectionForm({
		section: SECTION_KEY,
		schema: SuppliersTestingSchema,
		defaultValue: DEFAULT_VALUES,
		onValidSubmit: () => {
			const nextStepId = getNextFormStepId(STEP_ID)
			if (!nextStepId) return
			const nextHref = buildStepPathFromId(nextStepId, searchParams)
			void navigate(nextHref)
		},
	})

	const {
		values,
		setValues,
		setFieldValue,
		getFieldErrors,
		submit,
		status,
		hasStoredValue,
		clear,
	} = form

	const didNormalizeRef = useRef(false)
	useEffect(() => {
		if (didNormalizeRef.current) return
		const normalized = normalizeSuppliersTestingDraft(values)
		if (normalized && !suppliersTestingValuesEqual(normalized, values)) {
			didNormalizeRef.current = true
			setValues(normalized)
			return
		}
		didNormalizeRef.current = true
		if (!normalized && !suppliersTestingValuesEqual(DEFAULT_VALUES, values)) {
			setValues(DEFAULT_VALUES)
		}
	}, [setValues, values])

	const suppliersCollection = useEntityCollection<
		SuppliersTestingFormValues,
		SupplierEntry,
		SupplierDraft
	>({
		form,
		fieldPath: 'suppliers',
		definition: supplierDefinition,
		getFieldErrorPaths: getSupplierFieldErrorPaths,
	})
	const supplierItems = useMemo(
		() =>
			suppliersCollection.items.map(({ draft, errors }) => ({
				draft,
				errors,
			})),
		[suppliersCollection.items],
	)

	const routinesCollection = useEntityCollection<
		SuppliersTestingFormValues,
		TestRoutineEntry,
		TestRoutineDraft
	>({
		form,
		fieldPath: 'routineTests',
		definition: testRoutineDefinition,
		getFieldErrorPaths: getRoutineFieldErrorPaths,
	})
	const routineItems = useMemo(
		() =>
			routinesCollection.items.map(({ draft, errors }) => ({
				draft,
				errors,
			})),
		[routinesCollection.items],
	)

	const repositoryEntry = values.safetyRepository ?? DEFAULT_REPOSITORY_ENTRY
	const repositoryDraft = useMemo(
		() => repositoryEntryToDraft(repositoryEntry),
		[repositoryEntry],
	)

	const supplierErrors = getFieldErrors('suppliers')
	const routineErrors = getFieldErrors('routineTests')
	const repositorySystemErrors = getFieldErrors('safetyRepository.system')
	const repositoryLocationErrors = getFieldErrors('safetyRepository.location')

	const editingSupplierDraft =
		editingSupplierIndex !== null &&
		suppliersCollection.items[editingSupplierIndex]
			? suppliersCollection.items[editingSupplierIndex].draft
			: suppliersCollection.createDraft()
	const editingRoutineDraft =
		editingRoutineIndex !== null &&
		routinesCollection.items[editingRoutineIndex]
			? routinesCollection.items[editingRoutineIndex].draft
			: routinesCollection.createDraft()

	const handleAddSupplier = (draft: SupplierDraft) => {
		suppliersCollection.append(draft)
	}

	const handleEditSupplier = (draft: SupplierDraft) => {
		if (editingSupplierIndex === null) return
		suppliersCollection.replace(editingSupplierIndex, draft)
		setEditingSupplierIndex(null)
	}

	const handleRemoveSupplier = (index: number) => {
		suppliersCollection.remove(index)
		setEditingSupplierIndex(null)
	}

	const handleAddRoutine = (draft: TestRoutineDraft) => {
		routinesCollection.append(draft)
	}

	const handleEditRoutine = (draft: TestRoutineDraft) => {
		if (editingRoutineIndex === null) return
		routinesCollection.replace(editingRoutineIndex, draft)
		setEditingRoutineIndex(null)
	}

	const handleRemoveRoutine = (index: number) => {
		routinesCollection.remove(index)
		setEditingRoutineIndex(null)
	}

	const handleRepositorySystemChange = (value: RepositorySystemValue) => {
		setFieldValue('safetyRepository.system', value)
	}

	const handleRepositoryLocationChange = (value: string) => {
		setFieldValue('safetyRepository.location', value)
	}

	const resources = SUPPLIERS_TESTING_RESOURCES

	return (
		<Page
			title="Suppliers & Testing"
			description="Document key suppliers, where safety evidence lives, and the routine tests that uphold your substantiation."
			className="space-y-8"
		>
			<Form
				method="post"
				className="space-y-8"
				onSubmit={(event) => {
					event.preventDefault()
					void submit()
				}}
			>
				<Divider />
				<CriticalSuppliersSection
					resources={resources}
					suppliers={supplierItems}
					onAdd={handleAddSupplier}
					onEdit={(index) => setEditingSupplierIndex(index)}
					onRemove={handleRemoveSupplier}
					emptyState={
						supplierErrors.length > 0
							? 'Add at least one critical supplier.'
							: undefined
					}
				/>
				<SupplierEditOverlay
					open={editingSupplierIndex !== null}
					onOpenChange={(open) => {
						if (!open) {
							setEditingSupplierIndex(null)
						}
					}}
					onSubmit={handleEditSupplier}
					initialValue={editingSupplierDraft}
					onClosed={() => setEditingSupplierIndex(null)}
					onCancel={() => setEditingSupplierIndex(null)}
				/>

				<Divider />

				<SafetyRepositorySection
					resources={resources}
					repository={repositoryDraft}
					systemErrors={repositorySystemErrors}
					locationErrors={repositoryLocationErrors}
					onSystemChange={handleRepositorySystemChange}
					onLocationChange={handleRepositoryLocationChange}
				/>

				<Divider />

				<RoutineTestingSection
					resources={resources}
					routines={routineItems}
					onAdd={handleAddRoutine}
					onEdit={(index) => setEditingRoutineIndex(index)}
					onRemove={handleRemoveRoutine}
					emptyState={
						routineErrors.length > 0
							? 'Add at least one routine test.'
							: undefined
					}
				/>
				<TestRoutineEditOverlay
					open={editingRoutineIndex !== null}
					onOpenChange={(open) => {
						if (!open) {
							setEditingRoutineIndex(null)
						}
					}}
					onSubmit={handleEditRoutine}
					initialValue={editingRoutineDraft}
					onClosed={() => setEditingRoutineIndex(null)}
					onCancel={() => setEditingRoutineIndex(null)}
				/>

				{status === 'invalid' ? (
					<p className="text-destructive text-sm">
						Check the highlighted fields before continuing.
					</p>
				) : null}
				<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
					{hasStoredValue ? (
						<Button type="button" variant="outline" onClick={() => clear()}>
							Clear answers
						</Button>
					) : (
						<span />
					)}
					<Button type="submit" className="w-full sm:w-auto">
						Continue
					</Button>
				</div>
			</Form>
		</Page>
	)
}

function getSupplierFieldErrorPaths(index: number): readonly string[] {
	const prefix = `suppliers.${index}`
	return [
		prefix,
		`${prefix}.name`,
		`${prefix}.materialScope`,
		`${prefix}.riskLevel`,
		`${prefix}.qualityAgreementSigned`,
		`${prefix}.contactEmail`,
	]
}

function getRoutineFieldErrorPaths(index: number): readonly string[] {
	const prefix = `routineTests.${index}`
	return [
		prefix,
		`${prefix}.name`,
		`${prefix}.nameOther`,
		`${prefix}.frequency`,
	]
}

export function normalizeSuppliersTestingDraft(
	value: unknown,
): SuppliersTestingFormValues | null {
	if (!value || typeof value !== 'object') return null
	const input = value as Partial<SuppliersTestingFormValues> &
		Record<string, unknown>

	const supplierDrafts: SupplierDraft[] = Array.isArray(input.suppliers)
		? input.suppliers
				.map((candidate) => convertToSupplierDraft(candidate))
				.filter((draft): draft is SupplierDraft => draft !== null)
		: []
	const suppliers = supplierDrafts.map((draft) => supplierDraftToEntry(draft))

	const repositoryDraft = convertToRepositoryDraft(input.safetyRepository)
	const safetyRepository = repositoryDraftToEntry(repositoryDraft)

	const routineDrafts: TestRoutineDraft[] = Array.isArray(input.routineTests)
		? input.routineTests
				.map((candidate) => convertToTestRoutineDraft(candidate))
				.filter((draft): draft is TestRoutineDraft => draft !== null)
		: []
	const routineTests = routineDrafts.map((draft) =>
		testRoutineDraftToEntry(draft),
	)

	const candidate = {
		suppliers,
		safetyRepository,
		routineTests,
	}

	const parsed = SuppliersTestingSchema.safeParse(candidate)
	return parsed.success ? parsed.data : candidate
}

function suppliersTestingValuesEqual(
	left: SuppliersTestingFormValues,
	right: SuppliersTestingFormValues,
): boolean {
	return JSON.stringify(left) === JSON.stringify(right)
}

function convertToSupplierDraft(value: unknown): SupplierDraft | null {
	if (!value || typeof value !== 'object') return null
	const record = value as Record<string, unknown>
	const name = typeof record.name === 'string' ? record.name : ''
	const materialScope =
		typeof record.materialScope === 'string' ? record.materialScope : ''
	const template = createSupplierDraft()
	const rawRiskLevel = record.riskLevel
	const riskLevel =
		typeof rawRiskLevel === 'string' && isSupplierRiskLevelValue(rawRiskLevel)
			? rawRiskLevel
			: template.riskLevel
	const qualityAgreementSigned = toBoolean(record.qualityAgreementSigned)
	const contactEmail =
		typeof record.contactEmail === 'string' ? record.contactEmail : ''
	return createSupplierDraft({
		name,
		materialScope,
		riskLevel,
		qualityAgreementSigned,
		contactEmail,
	})
}

function convertToRepositoryDraft(value: unknown): RepositoryDraft {
	const defaultDraft = createRepositoryDraft()
	if (!value || typeof value !== 'object') return defaultDraft
	const record = value as Record<string, unknown>
	const system =
		typeof record.system === 'string' && isRepositorySystemValue(record.system)
			? record.system
			: defaultDraft.system
	const location =
		typeof record.location === 'string'
			? record.location
			: defaultDraft.location
	return sanitizeRepositoryDraft(createRepositoryDraft({ system, location }))
}

function convertToTestRoutineDraft(value: unknown): TestRoutineDraft | null {
	if (!value || typeof value !== 'object') return null
	const record = value as Record<string, unknown>
	const template = createTestRoutineDraft()
	const name =
		typeof record.name === 'string' && isRoutineTestNameValue(record.name)
			? record.name
			: template.name
	const nameOther = typeof record.nameOther === 'string' ? record.nameOther : ''
	const frequency =
		typeof record.frequency === 'string' &&
		isRoutineTestFrequencyValue(record.frequency)
			? record.frequency
			: template.frequency
	return createTestRoutineDraft({
		name,
		nameOther,
		frequency,
	})
}

function toBoolean(value: unknown): boolean {
	if (typeof value === 'boolean') return value
	if (typeof value === 'number') return value !== 0
	if (typeof value === 'string') {
		const normalized = value.trim().toLowerCase()
		return normalized === 'true' || normalized === '1' || normalized === 'on'
	}
	return false
}
